// Patent claims exercised: P-003 Claims 4-8 | P-005 Claims 1,8
import { useState } from 'react';

/**
 * useDomainValidation — shared hook for domain validation response handling.
 *
 * Usage in any form component:
 *   const { domainValidation, handleValidationResponse, clearValidation } = useDomainValidation();
 *
 *   // In your submit handler, after getting the response:
 *   if (!handleValidationResponse(response, 'recipe')) {
 *     return; // validation error handled — modal will show
 *   }
 *
 *   // In your JSX:
 *   {renderDomainValidationModal()}
 */
export function useDomainValidation() {
  const [domainValidation, setDomainValidation] = useState(null);

  /**
   * Checks a fetch response for 422/503 domain validation errors.
   * Returns true if response is OK (caller should continue).
   * Returns false if validation error was caught (modal state set, caller should stop).
   * Returns null if response is a different error (caller handles it).
   */
  const handleValidationResponse = async (response, entityType) => {
    if (response.ok) return true;

    if (response.status === 422) {
      const err = await response.json().catch(() => null);
      const detail = err?.detail || 'Validation failed';
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || d).join(', ')
          : 'Validation failed';
      setDomainValidation({ mode: 'rejection', message, entityType });
      return false;
    }

    if (response.status === 503) {
      const err = await response.json().catch(() => null);
      if (err?.detail === 'DOMAIN_VALIDATION_TIMEOUT') {
        setDomainValidation({ mode: 'timeout', entityType });
        return false;
      }
    }

    // Not a domain validation error — return null so caller handles it
    return null;
  };

  const clearValidation = () => setDomainValidation(null);

  return { domainValidation, handleValidationResponse, clearValidation };
}
